import type { MemberCycleKey, MemberPayMode, MemberVersion } from "./skus";
import { supportsSubscribe } from "./skus";
import { membershipTierRank, normalizePackageKey } from "@/utils/memberTier";

const CYCLE_RANK: Record<MemberCycleKey, number> = { month: 1, season: 2, year: 3 };

/** 「您已是」与升级按钮统一用 PRD 短名 VIP / SVIP / VVIP */
const VERSION_SHORT: Record<MemberVersion, string> = {
  vip: "VIP",
  svip: "SVIP",
  ultra: "VVIP"
};

export function buyoutSegmentLabel(cycle: MemberCycleKey): string {
  if (cycle === "month") return "月度买断";
  if (cycle === "season") return "季度买断";
  return "年度买断";
}

/** 切到不存在订阅档的卡片时强制买断，其它情况记住用户上次选择。 */
export function effectivePayMode(
  version: MemberVersion,
  cycle: MemberCycleKey,
  remembered: MemberPayMode
): MemberPayMode {
  return supportsSubscribe(version, cycle) ? remembered : "buyout";
}

export function resolveDefaultCycle(timeMode: string | undefined): MemberCycleKey {
  if (timeMode === "season") return "season";
  if (timeMode === "year" || timeMode === "year_v1") return "year";
  return "month";
}

export interface ActiveMemberOffer {
  version: MemberVersion;
  cycle: MemberCycleKey;
  payMode: MemberPayMode;
  /** 存量半月，不是新的月付档，不能当成同周期阻断 */
  legacyHalf: boolean;
}

export interface ActiveMemberPackage {
  memberType?: string;
  duration?: string | null;
  signNo?: string | null;
}

export function readActiveOfferFromPackages(
  highestPackage: string,
  packages: ActiveMemberPackage[]
): ActiveMemberOffer | null {
  const version = normalizePackageKey(highestPackage);
  if (version === "free") return null;
  const row = packages.find((item) => normalizePackageKey(item.memberType) === version);
  if (!row) return null;
  return readActiveOffer({ highestPackage: version, duration: row.duration, signNo: row.signNo });
}

export function readActiveOffer(input: {
  highestPackage: string;
  duration?: string | null;
  signNo?: string | null;
}): ActiveMemberOffer | null {
  if (input.highestPackage !== "vip" && input.highestPackage !== "svip" && input.highestPackage !== "ultra") {
    return null;
  }
  const duration = input.duration ?? "";
  const legacyHalf = /half/i.test(duration);
  const cycle: MemberCycleKey = /year/i.test(duration) ? "year" : /season/i.test(duration) ? "season" : "month";
  return {
    version: input.highestPackage,
    cycle,
    payMode: input.signNo ? "subscribe" : "buyout",
    legacyHalf
  };
}

/**
 * new：免费/过期开通
 * renew：买断同周期再买断（顺延，不退款）
 * subscribe：买断同周期转订阅（退款重签）
 * upgrade：跨版本或同版本更长周期
 * blocked：无法降级 / 您已是
 */
export type CardButtonAction = "new" | "renew" | "subscribe" | "upgrade" | "blocked";

export function resolveMemberCardButton(input: {
  version: MemberVersion;
  cycle: MemberCycleKey;
  payMode: MemberPayMode;
  active: ActiveMemberOffer | null;
}): { action: CardButtonAction; text: string; disabled: boolean } {
  const ownedLabel = VERSION_SHORT[input.active?.version ?? input.version];
  if (!input.active) {
    return { action: "new", text: input.payMode === "subscribe" ? "立即订阅" : "立即开通", disabled: false };
  }
  if (membershipTierRank(input.active.version) > membershipTierRank(input.version)) {
    return blocked("无法降级");
  }

  const sameVersion = input.active.version === input.version;
  const sameCycle = !input.active.legacyHalf && input.active.cycle === input.cycle;
  if (sameVersion && sameCycle) {
    // 订阅中：同周期任意形态均阻断（不能重复签，也不能转买断）
    if (input.active.payMode === "subscribe") {
      return blocked(`您已是${ownedLabel}会员`);
    }
    // 买断 → 同周期买断：立即续期
    if (input.payMode === "buyout") {
      return { action: "renew", text: "立即续期", disabled: false };
    }
    // 买断 → 同周期订阅：立即订阅（退款重签）
    return { action: "subscribe", text: "立即订阅", disabled: false };
  }

  // 同版本更短周期：阻断为「您已是」，不是「无法降级」
  if (sameVersion && !input.active.legacyHalf && CYCLE_RANK[input.cycle] < CYCLE_RANK[input.active.cycle]) {
    return blocked(`您已是${ownedLabel}会员`);
  }

  return {
    action: "upgrade",
    text: upgradeButtonText(input.version, input.cycle),
    disabled: false
  };
}

/** 新套餐按目标周期命名，SVIP 月付已统一月度，不沿用旧半月/月度合并列。 */
export function upgradeButtonText(version: MemberVersion, cycle: MemberCycleKey): string {
  const cycleLabel = cycle === "month" ? "月度" : cycle === "season" ? "季度" : "年度";
  return `升级为${cycleLabel}${VERSION_SHORT[version]}`;
}

function blocked(text: string): { action: CardButtonAction; text: string; disabled: boolean } {
  return { action: "blocked", text, disabled: true };
}
